import { CUSTOMER_TYPES } from "models";
import { isDateInDayRange, isSameDayHelper } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { Appointment } from "models";

export const MIN_SEARCH_LENGTH = 3;
export const DEBOUNCED_SEARCH_TIME = 500;
export const FIXED_VIN_LENGTH = 17;

type CustomerOrCompanyNameProps = {
  title?: string;
  firstname?: string;
  surname?: string;
  company?: string;
};

type IsAppointmentDateInRangeParams = {
  appointmentDate: string;
  selectedDate: string | Date;
  startDate?: string | Date;
  endDate?: string | null;
};

type IsAppointmentInSelectedListParams = Omit<IsAppointmentDateInRangeParams, "appointmentDate"> & {
  appointment: Appointment;
  listByScheduledRange: boolean;
};

export const getCustomerOrCompanyName = ({ company, firstname, surname, title }: CustomerOrCompanyNameProps) => {
  if (title || firstname || surname) return [title, firstname, surname].filter(Boolean).join(" ");
  if (company) return company;

  return "";
};

export const isEmptyStringField = (value: string | undefined) => !value || value.trim() === "";

export function hasAnyFieldValue<T extends object>(obj: T | null | undefined, fields: (keyof T)[]): boolean {
  if (!obj) return false;
  return fields.some(field => Boolean(obj[field]));
}

export const formatRegistrationNumber = (reg: string) => {
  if (reg.length < 2) return reg;

  let newReg = reg[0];
  for (let i = 1; i < reg.length; i++) {
    if (reg[i - 1] !== "-" && reg[i] !== "-" && /\d/.test(reg[i - 1]) !== /\d/.test(reg[i])) newReg += "-";
    newReg += reg[i];
  }

  return newReg;
};

export const generateRandomString = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

export const getCustomerTypeLabel = (t: TFunction, customerType: CUSTOMER_TYPES) => {
  if (customerType === CUSTOMER_TYPES.Driver) return t("v8_driver", "Driver");
  if (customerType === CUSTOMER_TYPES.Owner) return t("v8_owner", "Owner");
  if (customerType === CUSTOMER_TYPES.Contractor) return t("v8_contractor", "Contractor");

  return null;
};

export const isAppointmentDateInRange = ({ appointmentDate, startDate, endDate, selectedDate }: IsAppointmentDateInRangeParams) =>
  endDate && startDate && startDate !== endDate ? isDateInDayRange(appointmentDate, startDate, endDate) : isSameDayHelper(appointmentDate, selectedDate);

export const isAppointmentInSelectedList = ({ appointment, listByScheduledRange, ...selectedRange }: IsAppointmentInSelectedListParams) => {
  const { startDate, endDate, selectedDate } = selectedRange;
  const isSingleDayList = !startDate || !endDate || startDate === endDate;
  if (appointment.is_pinned && isSingleDayList && isSameDayHelper(selectedDate)) return true;
  if (isAppointmentDateInRange({ appointmentDate: appointment.time_car_app, ...selectedRange })) return true;
  if (!listByScheduledRange) return false;

  return [appointment.due_in, appointment.car_return_time, appointment.car_in_shop].some(
    date => date && isAppointmentDateInRange({ appointmentDate: date, ...selectedRange })
  );
};
